import React from 'react'
import { Card, CardContent, CardHeader, Label, Select, Switch } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { listPlans } from '@/modules/plans/services/planService'
import type { Plan } from '@/modules/plans/types'
import {
  fetchPlanVisionPolicies,
  fetchRoleVisionPolicies,
  upsertPlanVisionPolicy,
  upsertRoleVisionPolicy,
} from '../services/visionAccessService'
import {
  getVisionLockedPeopleConfig,
  setVisionLockedPeopleConfig,
  type VisionLockedPeopleMode,
} from '../services/visionLockedCardSettingsService'

export default function VisionSettings() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [rolePolicies, setRolePolicies] = React.useState<Record<string, boolean>>({})
  const [planPolicies, setPlanPolicies] = React.useState<Record<string, boolean>>({})
  const [plans, setPlans] = React.useState<Plan[]>([])
  const [peopleEnabled, setPeopleEnabled] = React.useState(false)
  const [peopleMode, setPeopleMode] = React.useState<VisionLockedPeopleMode>('messages')

  React.useEffect(() => {
    let mounted = true
    async function load() {
      try {
        const [roles, plansPolicies, plansResult, peopleCfg] = await Promise.all([
          fetchRoleVisionPolicies(),
          fetchPlanVisionPolicies(),
          listPlans({ page: 1, pageSize: 100 }),
          getVisionLockedPeopleConfig(),
        ])
        if (!mounted) return
        const rolesMap: Record<string, boolean> = {}
        roles.forEach((r) => {
          rolesMap[r.role] = !!r.vision_enabled
        })
        const plansMap: Record<string, boolean> = {}
        plansPolicies.forEach((p) => {
          plansMap[p.plan_id] = !!p.vision_enabled
        })
        setRolePolicies(rolesMap)
        setPlanPolicies(plansMap)
        setPlans(plansResult.items)
        setPeopleEnabled(peopleCfg.enabled)
        setPeopleMode(peopleCfg.mode)
      } catch (e: any) {
        toast({
          title: 'Gerenciar Vis&otilde;es',
          description: e?.message || 'Falha ao carregar configura&ccedil;&otilde;es',
          variant: 'error',
        })
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [toast])

  async function toggleRole(role: string, next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await upsertRoleVisionPolicy(role, next)
      setRolePolicies((prev) => ({ ...prev, [role]: next }))
      toast({
        title: 'Gerenciar Vis&otilde;es (Roles)',
        description: `Vis&otilde;es para role "${role}" ${next ? 'ativadas' : 'desativadas'}.`,
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Gerenciar Vis&otilde;es (Roles)',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function togglePlan(planId: string, next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await upsertPlanVisionPolicy(planId, next)
      setPlanPolicies((prev) => ({ ...prev, [planId]: next }))
      toast({
        title: 'Gerenciar Vis&otilde;es (Planos)',
        description: `Vis&otilde;es para plano "${planId}" ${next ? 'ativadas' : 'desativadas'}.`,
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Gerenciar Vis&otilde;es (Planos)',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function updatePeopleConfig(nextEnabled: boolean, nextMode: VisionLockedPeopleMode) {
    if (saving) return
    setSaving(true)
    try {
      await setVisionLockedPeopleConfig({ enabled: nextEnabled, mode: nextMode })
      setPeopleEnabled(nextEnabled)
      setPeopleMode(nextMode)
      toast({
        title: 'Gerenciar Vis&otilde;es (Card bloqueado)',
        description: 'Configuracao atualizada com sucesso.',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Gerenciar Vis&otilde;es (Card bloqueado)',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Gerenciar Vis&otilde;es</CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Acesso por papel (role)</div>
          <p className="text-xs text-foreground/60">
            Defina quais pap&eacute;is podem visualizar e usar o m&oacute;dulo de Vis&otilde;es. O acesso efetivo considera role + plano.
          </p>
          <div className="mt-2 space-y-2">
            {['master', 'member', 'afiliado'].map((role) => (
              <div
                key={role}
                className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
              >
                <div className="text-sm font-medium capitalize">{role}</div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Desativado</Label>
                  <Switch
                    checked={!!rolePolicies[role]}
                    disabled={loading || saving}
                    onChange={(e) => toggleRole(role, (e.target as HTMLInputElement).checked)}
                  />
                  <Label className="text-xs">Ativado</Label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Acesso por plano</div>
          <p className="text-xs text-foreground/60">
            Ative o m&oacute;dulo de Vis&otilde;es apenas para planos espec&iacute;ficos. O acesso efetivo considera role + plano.
          </p>
          <div className="mt-2 space-y-2">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
              >
                <div className="text-sm font-medium">
                  {plan.name}{' '}
                  <span className="text-xs text-foreground/60">
                    ({plan.id})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Desativado</Label>
                  <Switch
                    checked={!!planPolicies[plan.id]}
                    disabled={loading || saving}
                    onChange={(e) => togglePlan(plan.id, (e.target as HTMLInputElement).checked)}
                  />
                  <Label className="text-xs">Ativado</Label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Card bloqueado (visoes)</div>
          <p className="text-xs text-foreground/60">
            Controla se o card de visao bloqueada exibe o contador e qual tipo de contagem usar.
          </p>
          <div className="mt-2 space-y-3">
            <div className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2">
              <div className="text-sm font-medium">Exibir contador</div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Desativado</Label>
                <Switch
                  checked={peopleEnabled}
                  disabled={loading || saving}
                  onChange={(e) => updatePeopleConfig((e.target as HTMLInputElement).checked, peopleMode)}
                />
                <Label className="text-xs">Ativado</Label>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-xs text-foreground/60">Tipo de contagem</Label>
              <Select
                className="max-w-[220px]"
                value={peopleMode}
                disabled={loading || saving || !peopleEnabled}
                onChange={(e) => updatePeopleConfig(peopleEnabled, (e.target as HTMLSelectElement).value as VisionLockedPeopleMode)}
              >
                <option value="messages">Mensagens</option>
                <option value="avatars">Avatares</option>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
